import React, { useRef } from 'react';
import { Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
export function MeetingsFlow() {
  const contentRef = useRef<HTMLDivElement>(null);
  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save('1hire-meeting-flow.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };
  const meetings = [{
    phase: 'Onboarding',
    name: 'Pre-Build Meeting',
    duration: '30 mins',
    attendees: 'PS, Reina',
    purpose: 'Kickoff; build one-pager; align priorities with Manny; prep for build'
  }, {
    phase: 'Onboarding',
    name: 'Process Alignment Call',
    duration: '1 hr (standard) / 90 mins (special)',
    attendees: 'JS, Recruiter, HR, VP of Sales',
    purpose: 'Deep dive on recruiting process; finalize one-pager; alignment before build'
  }, {
    phase: 'Operations Sync',
    name: 'ATS Setup & Integration',
    duration: '45 mins',
    attendees: 'Shaela, Maranda, HR, VP of Sales, Recruiter, ATS users',
    purpose: 'Connect ATS; verify permissions; workflow setup\n\n[After system build]'
  }, {
    phase: 'Operations Sync',
    name: 'How to Interview Training',
    duration: '1 hr',
    attendees: 'Zackery, JS, Interviewers',
    purpose: 'Train team on interview process, scoring & expectations\n\n[After system build]'
  }, {
    phase: 'Post-Launch',
    name: 'Week 1 Check-in',
    duration: '30 mins',
    attendees: 'JS & Shaela',
    purpose: 'Review first-week performance; fix early bottlenecks'
  }, {
    phase: 'Post-Launch',
    name: 'Week 2 Review',
    duration: '45 mins',
    attendees: 'JS & Zackery',
    purpose: 'Evaluate candidate flow; improve interview conversions'
  }, {
    phase: 'Post-Launch',
    name: 'Week 3 Check-in',
    duration: '45 mins',
    attendees: 'JS & SG',
    purpose: 'Review performance; adjust systems & scripts'
  }, {
    phase: 'Post-Launch',
    name: 'Week 4 Evaluation',
    duration: '45 mins (assumed)',
    attendees: 'JS & PS',
    purpose: 'Month-end review; long-term strategy & success plan'
  }];
  return <div className="w-full min-h-screen bg-white p-4 md:p-8 lg:p-12" data-id="element-1441">
      <div className="max-w-7xl mx-auto" data-id="element-1442">
        <div ref={contentRef} data-id="element-1443">
          {/* Header */}
          <div className="mb-6 md:mb-10" data-id="element-1444">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-1445">
              <div data-id="element-1446">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6pReh5Z3t5UY8MLtuwdsRF/Hiring_Interviews_%283%29.png" alt="1HIRE" className="h-10 md:h-12 mb-4 md:mb-6" data-id="element-1447" />
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-1448">
                  Client Onboarding Meeting Flow
                </h1>
                <p className="text-slate-600 mt-2 text-sm md:text-base" data-id="element-1449">
                  Structured engagement process from kickoff to launch
                </p>
              </div>
              <button onClick={handleDownloadPDF} className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-sm md:text-base whitespace-nowrap" data-id="element-1450">
                <Download size={18} data-id="element-1451" />
                Download PDF
              </button>
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block border border-slate-200 rounded-lg overflow-hidden" data-id="element-1452">
            <table className="w-full" data-id="element-1453">
              <thead data-id="element-1454">
                <tr className="bg-slate-900 text-white" data-id="element-1455">
                  <th className="px-6 py-4 text-left font-semibold text-sm" data-id="element-1456">
                    Phase
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-sm" data-id="element-1457">
                    Meeting Name
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-sm" data-id="element-1458">
                    Duration
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-sm" data-id="element-1459">
                    Attendees
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-sm" data-id="element-1460">
                    Purpose / Notes
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1461">
                {meetings.map((meeting, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-1462">
                    <td className="px-6 py-4 font-semibold text-slate-900 border-t border-slate-200" data-id="element-1463">
                      {meeting.phase}
                    </td>
                    <td className="px-6 py-4 text-slate-900 border-t border-slate-200" data-id="element-1464">
                      {meeting.name}
                    </td>
                    <td className="px-6 py-4 text-slate-700 border-t border-slate-200 whitespace-nowrap" data-id="element-1465">
                      {meeting.duration}
                    </td>
                    <td className="px-6 py-4 text-slate-700 border-t border-slate-200" data-id="element-1466">
                      {meeting.attendees}
                    </td>
                    <td className="px-6 py-4 text-slate-700 border-t border-slate-200 whitespace-pre-line" data-id="element-1467">
                      {meeting.purpose}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4" data-id="element-1468">
            {meetings.map((meeting, index) => <div key={index} className="bg-white border-2 border-slate-200 rounded-lg p-4 shadow-sm" data-id="element-1469">
                <div className="flex items-start justify-between mb-3" data-id="element-1470">
                  <span className="inline-block px-3 py-1 bg-slate-900 text-white text-xs font-semibold rounded" data-id="element-1471">
                    {meeting.phase}
                  </span>
                  <span className="text-sm text-slate-600 font-medium" data-id="element-1472">
                    {meeting.duration}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-1473">
                  {meeting.name}
                </h3>

                <div className="mb-3" data-id="element-1474">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-1475">
                    Attendees
                  </div>
                  <div className="text-sm text-slate-700" data-id="element-1476">
                    {meeting.attendees}
                  </div>
                </div>

                <div data-id="element-1477">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-1478">
                    Purpose / Notes
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed" data-id="element-1479">
                    {meeting.purpose}
                  </div>
                </div>
              </div>)}
          </div>

          {/* Footer Note */}
          <div className="mt-6 md:mt-8 text-xs md:text-sm text-slate-600" data-id="element-1480">
            <p className="font-medium mb-2" data-id="element-1481">Key Timeline Notes:</p>
            <ul className="space-y-1 list-disc list-inside" data-id="element-1482">
              <li data-id="element-1483">
                Pre-Build Meeting is the only meeting available before payment
              </li>
              <li data-id="element-1484">
                ATS Setup and Interview Training occur after system build
                completion
              </li>
              <li data-id="element-1485">
                Post-launch check-ins ensure smooth adoption and optimization
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>;
}